import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Users, CheckCircle2, Clock, AlertCircle, 
  DollarSign, TrendingUp, Shield, Settings,
  UserPlus, FileText, Activity, BarChart3, Trash2, Loader2, UserMinus,
  Mail, AlertTriangle, CheckCircle, Zap, ArrowUpRight, ArrowDownRight, GripVertical
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { performanceAPI, tasksAPI, teamAPI, leaveAPI, systemAPI, bandwidthAPI } from '../services/api';
import apiClient from '../services/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BandwidthBadge from '../components/BandwidthBadge';
import { triggerNotificationPrompt } from '../components/NotificationOnboarding';
import { motion } from 'framer-motion';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [deleteUserEmail, setDeleteUserEmail] = useState('');
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [vaBandwidth, setVaBandwidth] = useState([]);
  const [showReserveDialog, setShowReserveDialog] = useState(false);
  const [selectedVAForReserve, setSelectedVAForReserve] = useState(null);
  const [reservePlan, setReservePlan] = useState('professional');
  const [reserveLoading, setReserveLoading] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'client' });
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [widgetLayout, setWidgetLayout] = useState(['stats', 'quick_actions', 'advanced_tools', 'bandwidth', 'activity_approvals']);
  const [draggedWidget, setDraggedWidget] = useState(null);
  const [layoutDirty, setLayoutDirty] = useState(false);
  const [approveLoading, setApproveLoading] = useState(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Load saved widget layout
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard-layout`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.layout?.length) setWidgetLayout(data.layout);
        }
      } catch (_) {}
    };
    loadLayout();
  }, []);

  const saveLayout = async (newLayout) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard-layout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ layout: newLayout, hidden: [] })
      });
    } catch (_) {}
  };

  const handleWidgetDragStart = (widgetId) => setDraggedWidget(widgetId);
  const handleWidgetDragOver = (e) => e.preventDefault();
  const handleWidgetDrop = (targetId) => {
    if (!draggedWidget || draggedWidget === targetId) return;
    const newLayout = [...widgetLayout];
    const fromIdx = newLayout.indexOf(draggedWidget);
    const toIdx = newLayout.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    newLayout.splice(fromIdx, 1);
    newLayout.splice(toIdx, 0, draggedWidget);
    setWidgetLayout(newLayout);
    setDraggedWidget(null);
    saveLayout(newLayout);
  };

  const handleApproveLeave = async (leaveId) => {
    setApproveLoading(leaveId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leave/${leaveId}/approve`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to approve');
      toast.success('Leave request approved');
      setPendingLeaves(prev => prev.filter(l => (l.id || l._id) !== leaveId));
    } catch (err) {
      toast.error('Failed to approve leave request');
    } finally {
      setApproveLoading(null);
    }
  };

  const handleRejectLeave = async (leaveId) => {
    setApproveLoading(leaveId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leave/${leaveId}/reject`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to reject');
      toast.success('Leave request rejected');
      setPendingLeaves(prev => prev.filter(l => (l.id || l._id) !== leaveId));
    } catch (err) {
      toast.error('Failed to reject leave request');
    } finally {
      setApproveLoading(null);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchEmailStatus();
    fetchVABandwidth();
    triggerNotificationPrompt();
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const response = await systemAPI.getEmailStatus();
      setEmailStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch email status:', error);
      setEmailStatus({ configured: false, valid: false, message: 'Unable to check email status' });
    }
  };

  const fetchVABandwidth = async () => {
    try {
      const response = await bandwidthAPI.getAllBandwidth();
      setVaBandwidth(response.data.bandwidth || []);
    } catch (error) {
      console.error('Failed to fetch VA bandwidth:', error);
    }
  };

  const handleReserveVA = async () => {
    if (!selectedVAForReserve) return;
    
    setReserveLoading(true);
    try {
      const response = await bandwidthAPI.allocateVA({
        va_id: selectedVAForReserve.va_id,
        plan: reservePlan,
        notes: 'Admin reservation'
      });
      toast.success(response.data.message);
      setShowReserveDialog(false);
      setSelectedVAForReserve(null);
      fetchVABandwidth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reserve VA');
    } finally {
      setReserveLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setAddUserLoading(true);
    try {
      await apiClient.post('/admin/users/create', newUser);
      toast.success(`User ${newUser.name} created successfully`);
      setShowAddUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'client' });
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddUserLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Single batch call for all dashboard data - much faster than 6+ separate calls
      const batchRes = await apiClient.get('/admin/dashboard-batch');
      const batch = batchRes.data;

      const allTasks = batch.tasks || [];
      const allTeam = batch.team || [];
      const allLeaves = batch.leaves || [];
      const usersData = batch.users || {};
      const revenueData = batch.revenue || { monthly_revenue: 0, revenue_change: "0%" };

      setStats({
        totalUsers: usersData.total || 0,
        totalVAs: usersData.va_count || 0,
        totalClients: usersData.client_count || 0,
        activeVAs: allTeam.filter(m => m.status === 'active').length || usersData.va_count || 0,
        totalTasks: allTasks.length,
        completedTasks: allTasks.filter(t => t.status === 'completed').length,
        pendingTasks: allTasks.filter(t => t.status === 'pending').length,
        pendingLeaves: allLeaves.filter(l => {
          const s = l.status || l.approval_status || 'pending';
          return s === 'pending' || (!l.status && !l.approval_status);
        }).length,
        monthlyRevenue: revenueData.monthly_revenue || 0,
        revenueChange: revenueData.revenue_change || "0%",
        avgVARating: revenueData.avg_va_rating || '5.0',
        avgResponseTime: revenueData.avg_response_time || '0h',
        currencyBreakdown: revenueData.currency_breakdown || {},
        activeSubscriptions: revenueData.active_subscriptions || 0
      });

      // Recent activity - compute from actual data
      const activityItems = [];
      
      // Recent tasks
      const recentTasks = [...allTasks]
        .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
        .slice(0, 5);
      recentTasks.forEach(task => {
        const vaName = task.assigned_to_name || '';
        const assignedLabel = vaName ? ` to ${vaName}` : '';
        activityItems.push({
          type: task.status === 'completed' ? 'task_completed' : 'task_created',
          message: task.status === 'completed' 
            ? `Task "${task.title}" completed${vaName ? ` by ${vaName}` : ''}` 
            : `Task "${task.title}" assigned${assignedLabel}`,
          time: task.updated_at || task.created_at,
          icon: task.status === 'completed' ? 'check' : 'task'
        });
      });
      
      // Recent leaves
      allLeaves.slice(0, 3).forEach(leave => {
        const requester = leave.user_name || leave.va_name || '';
        activityItems.push({
          type: 'leave_request',
          message: `Leave request: ${leave.leave_type || 'Time off'}${requester ? ` by ${requester}` : ''} (${leave.status})`,
          time: leave.created_at,
          icon: 'calendar'
        });
      });

      // Recent payment notifications from in-app notifications
      try {
        const notifRes = await apiClient.get('/in-app-notifications');
        const paymentNotifs = (notifRes.data || [])
          .filter(n => n.type === 'payment' || n.notification_type === 'payment')
          .slice(0, 5);
        paymentNotifs.forEach(n => {
          activityItems.push({
            type: 'payment',
            message: n.message || n.title || 'Payment received',
            time: n.created_at,
            icon: 'payment'
          });
        });
      } catch (_) {}
      
      // Sort by time descending and take top 10
      activityItems.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
      setRecentActivity(activityItems.slice(0, 10));

      setPendingLeaves(allLeaves.filter(l => {
        const s = l.status || l.approval_status || 'pending';
        return s === 'pending' || (!l.status && !l.approval_status);
      }).slice(0, 5));

      // Set bandwidth from batch data
      if (batch.bandwidth) {
        setVaBandwidth(batch.bandwidth);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Fallback: try individual endpoints if batch fails
      try {
        const [tasksRes, teamRes, leaveRes] = await Promise.all([
          tasksAPI.getAll(),
          teamAPI.getAll(),
          leaveAPI.getAll()
        ]);
        const allTasks = tasksRes.data || [];
        const allTeam = teamRes.data || [];
        const allLeaves = leaveRes.data || [];
        setStats({
          totalUsers: allTeam.length + 5,
          totalVAs: allTeam.length,
          totalClients: 5,
          activeVAs: allTeam.filter(m => m.status === 'active').length,
          totalTasks: allTasks.length,
          completedTasks: allTasks.filter(t => t.status === 'completed').length,
          pendingTasks: allTasks.filter(t => t.status === 'pending').length,
          pendingLeaves: allLeaves.filter(l => {
          const s = l.status || l.approval_status || 'pending';
          return s === 'pending' || (!l.status && !l.approval_status);
        }).length,
          monthlyRevenue: 0,
          revenueChange: "0%",
          avgVARating: '5.0',
          avgResponseTime: '0h',
          currencyBreakdown: {}
        });
        setRecentActivity([]);
        setPendingLeaves(allLeaves.filter(l => {
        const s = l.status || l.approval_status || 'pending';
        return s === 'pending' || (!l.status && !l.approval_status);
      }).slice(0, 5));
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupDemoData = async () => {
    setCleanupLoading(true);
    try {
      const response = await apiClient.post('/admin/cleanup-demo-data');
      if (response.data.success) {
        toast.success(`Demo data cleaned! Removed ${response.data.deleted?.demo_users_deleted || 0} demo users.`);
        setShowCleanupDialog(false);
        // Refresh dashboard data
        fetchDashboardData();
        fetchVABandwidth();
      }
    } catch (error) {
      console.error('Cleanup failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to clean demo data');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleSeedDemoData = async () => {
    setSeedLoading(true);
    try {
      const response = await apiClient.post('/admin/seed-demo-data');
      if (response.data.success) {
        toast.success(`Demo data created! ${response.data.created?.users_created || 0} users, ${response.data.created?.tasks_created || 0} tasks.`);
        setShowSeedDialog(false);
        // Refresh dashboard data
        fetchDashboardData();
        fetchVABandwidth();
      }
    } catch (error) {
      console.error('Seed failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to seed demo data');
    } finally {
      setSeedLoading(false);
    }
  };

  const handleDeleteUserByEmail = async () => {
    if (!deleteUserEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setDeleteUserLoading(true);
    try {
      const response = await apiClient.post('/admin/delete-user-by-email', {
        email: deleteUserEmail.trim()
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowDeleteUserDialog(false);
        setDeleteUserEmail('');
        // Refresh dashboard data
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Delete user failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  const handleForceCleanEmail = async () => {
    if (!deleteUserEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setDeleteUserLoading(true);
    try {
      const response = await apiClient.post('/admin/force-clean-email', {
        email: deleteUserEmail.trim()
      });
      if (response.data.success) {
        toast.success(`Email cleaned! ${response.data.total_records_deleted} records removed.`);
        setShowDeleteUserDialog(false);
        setDeleteUserEmail('');
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Force clean failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to clean email');
    } finally {
      setDeleteUserLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="admin-dashboard-skeleton">
        {/* Loading indicator */}
        <div className="flex items-center justify-center py-4">
          <div className="flex items-center gap-3">
            <Loader2 className="w-8 h-8 text-[#5FD068] animate-spin" />
            <span className="text-[#4B5563] text-sm font-medium">Loading dashboard...</span>
          </div>
        </div>
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48 bg-gray-200" />
            <Skeleton className="h-4 w-72 bg-gray-100" />
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-10 w-32 bg-gray-200" />
            <Skeleton className="h-10 w-28 bg-gray-200" />
          </div>
        </div>
        {/* Stats grid skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => (
            <Card key={i} className="border border-[#E5E7EB]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="space-y-3 flex-1">
                    <Skeleton className="h-3 w-20 bg-gray-200" />
                    <Skeleton className="h-7 w-16 bg-gray-200" />
                    <Skeleton className="h-3 w-28 bg-gray-100" />
                  </div>
                  <Skeleton className="h-10 w-10 rounded-lg bg-gray-200" />
                </div>
                <Skeleton className="h-8 w-full mt-4 rounded bg-gray-100" />
              </CardContent>
            </Card>
          ))}
        </div>
        {/* Quick actions skeleton */}
        <Card className="border border-[#E5E7EB]">
          <CardContent className="p-6">
            <Skeleton className="h-5 w-28 mb-4 bg-gray-200" />
            <div className="grid grid-cols-5 gap-4">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20 rounded-lg bg-gray-100" />)}
            </div>
          </CardContent>
        </Card>
        {/* Bottom cards skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1,2].map(i => (
            <Card key={i} className="border border-[#E5E7EB]">
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-5 w-32 bg-gray-200" />
                {[1,2,3].map(j => <Skeleton key={j} className="h-14 w-full rounded-lg bg-gray-100" />)}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Mini sparkline data for stat cards
  const sparklineData = {
    users: [{ v: 3 }, { v: 5 }, { v: 4 }, { v: 7 }, { v: 6 }, { v: 8 }, { v: stats?.totalUsers || 9 }],
    vas: [{ v: 1 }, { v: 2 }, { v: 2 }, { v: 3 }, { v: 3 }, { v: 4 }, { v: stats?.activeVAs || 4 }],
    tasks: [{ v: 2 }, { v: 5 }, { v: 8 }, { v: 6 }, { v: 10 }, { v: 12 }, { v: stats?.totalTasks || 14 }],
    revenue: [{ v: 200 }, { v: 350 }, { v: 300 }, { v: 500 }, { v: 450 }, { v: 600 }, { v: stats?.monthlyRevenue || 700 }],
  };

  const statsArray = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      subtitle: `${stats?.totalVAs || 0} VAs, ${stats?.totalClients || 0} Clients`,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      sparkColor: '#3B82F6',
      sparkData: sparklineData.users,
      link: '/admin/users'
    },
    {
      title: 'Active VAs',
      value: stats?.activeVAs || 0,
      subtitle: 'Currently working',
      icon: Activity,
      color: 'text-[#5FD068]',
      bgColor: 'bg-[#E5F7E7]',
      sparkColor: '#5FD068',
      sparkData: sparklineData.vas,
      link: '/team'
    },
    {
      title: 'Total Tasks',
      value: stats?.totalTasks || 0,
      subtitle: `${stats?.completedTasks || 0} completed`,
      icon: CheckCircle2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      sparkColor: '#9333EA',
      sparkData: sparklineData.tasks,
      link: '/tasks'
    },
    {
      title: 'Monthly Revenue',
      value: `CA$${stats?.monthlyRevenue?.toLocaleString() || '0'}`,
      subtitle: stats?.currencyBreakdown && Object.keys(stats.currencyBreakdown).length > 0
        ? Object.entries(stats.currencyBreakdown).map(([cur, data]) => 
            `${cur}: ${data.total.toLocaleString()} (${data.count} txn${data.count > 1 ? 's' : ''})`
          ).join(' | ')
        : (stats?.revenueChange || '+0%'),
      icon: DollarSign,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      sparkColor: '#059669',
      sparkData: sparklineData.revenue,
      link: '/admin/analytics'
    }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'user_joined': return <UserPlus className="w-4 h-4 text-blue-500" />;
      case 'task_completed': return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'payment': return <DollarSign className="w-4 h-4 text-purple-500" />;
      case 'leave_request': return <Clock className="w-4 h-4 text-orange-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#5FD068]" />
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0D0A]">
              Admin Dashboard
            </h1>
          </div>
          <p className="text-[#4B5563] mt-1 text-sm">
            Complete system overview and management
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3 flex-wrap">
          <Button 
            variant="outline" 
            className="text-[#EF4444] border-red-200 hover:bg-red-50 text-xs sm:text-sm px-2 sm:px-4"
            onClick={() => setShowCleanupDialog(true)}
            data-testid="admin-cleanup-btn"
          >
            <Trash2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Clean Demo</span>
          </Button>
          <Button 
            variant="outline" 
            className="text-[#F59E0B] border-orange-200 hover:bg-orange-50 text-xs sm:text-sm px-2 sm:px-4"
            onClick={() => setShowDeleteUserDialog(true)}
            data-testid="admin-delete-user-btn"
          >
            <UserMinus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Delete User</span>
          </Button>
          <Link to="/settings">
            <Button variant="outline" className="text-xs sm:text-sm px-2 sm:px-4" data-testid="admin-settings-btn">
              <Settings className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid with Sparklines */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statsArray.map((stat, index) => {
          const Icon = stat.icon;
          const CardWrapper = stat.link ? Link : 'div';
          const cardProps = stat.link ? { to: stat.link } : {};
          
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08, duration: 0.35 }}
            >
              <CardWrapper {...cardProps} className="block">
                <Card className="border border-[#E5E7EB] hover:border-[#5FD068]/30 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group overflow-hidden h-full" data-testid={`admin-stat-card-${index}`}>
                  <CardContent className="p-3 sm:p-5">
                    <div className="flex items-start justify-between mb-3 min-w-0">
                      <div className="min-w-0 flex-1 mr-2">
                        <p className="text-[10px] sm:text-xs font-medium uppercase tracking-[0.1em] text-[#9CA3AF] truncate">{stat.title}</p>
                        <p className="text-lg sm:text-2xl font-medium text-[#0A0D0A] mt-1 tracking-tight truncate">{stat.value}</p>
                      </div>
                      <div className={`${stat.bgColor} p-2 rounded-lg group-hover:scale-105 transition-transform duration-200`}>
                        <Icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                    </div>
                    {/* Sparkline */}
                    <div className="h-8 mt-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={stat.sparkData}>
                          <defs>
                            <linearGradient id={`grad-${index}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={stat.sparkColor} stopOpacity={0.2} />
                              <stop offset="100%" stopColor={stat.sparkColor} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <Area type="monotone" dataKey="v" stroke={stat.sparkColor} strokeWidth={1.5} fill={`url(#grad-${index})`} dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <p className="text-xs text-[#4B5563] mt-1 truncate">{stat.subtitle}</p>
                  </CardContent>
                </Card>
              </CardWrapper>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Admin Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
            <Link to="/admin/users">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2" data-testid="admin-manage-users-btn">
                <Users className="w-5 h-5 text-blue-500" />
                <span className="text-sm">Manage Users</span>
              </Button>
            </Link>
            <Button 
              variant="outline" 
              className="w-full h-20 flex flex-col items-center justify-center gap-2 border-dashed border-2" 
              onClick={() => setShowAddUserDialog(true)}
              data-testid="admin-add-user-btn"
            >
              <UserPlus className="w-5 h-5 text-green-500" />
              <span className="text-sm">Add User</span>
            </Button>
            <Link to="/tasks">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2" data-testid="admin-all-tasks-btn">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-sm">All Tasks</span>
              </Button>
            </Link>
            <Link to="/leave">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2" data-testid="admin-leave-requests-btn">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-sm">Leave Requests</span>
                {stats?.pendingLeaves > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {stats.pendingLeaves}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/billing">
              <Button variant="outline" className="w-full h-20 flex flex-col items-center justify-center gap-2" data-testid="admin-billing-btn">
                <DollarSign className="w-5 h-5 text-purple-500" />
                <span className="text-sm">Billing</span>
              </Button>
            </Link>
          </div>
          
          {/* Advanced Admin Tools */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm font-medium text-gray-600 mb-3">Advanced Tools</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <Link to="/admin/advanced-analytics">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-analytics-btn">
                  <BarChart3 className="w-5 h-5 text-indigo-500" />
                  <span className="text-xs">Analytics</span>
                </Button>
              </Link>
              <Link to="/admin/va-matching">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-va-matching-btn">
                  <UserPlus className="w-5 h-5 text-emerald-500" />
                  <span className="text-xs">VA Matching</span>
                </Button>
              </Link>
              <Link to="/admin/va-scheduling">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-va-scheduling-btn">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <span className="text-xs">VA Scheduling</span>
                </Button>
              </Link>
              <Link to="/admin/weekly-digest">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-weekly-digest-btn">
                  <Mail className="w-5 h-5 text-cyan-500" />
                  <span className="text-xs">Weekly Digest</span>
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-3">
              <Link to="/admin/email-templates">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-email-templates-btn">
                  <FileText className="w-5 h-5 text-orange-500" />
                  <span className="text-xs">Email Templates</span>
                </Button>
              </Link>
              <Link to="/admin/va-management">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-va-mgmt-btn">
                  <Users className="w-5 h-5 text-pink-500" />
                  <span className="text-xs">VA Management</span>
                </Button>
              </Link>
              <Link to="/admin/bulk-assign">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-bulk-assign-btn">
                  <Activity className="w-5 h-5 text-purple-500" />
                  <span className="text-xs">Bulk Assign</span>
                </Button>
              </Link>
              <Link to="/admin/va-comparison">
                <Button variant="outline" className="w-full h-16 flex flex-col items-center justify-center gap-1" data-testid="admin-va-compare-btn">
                  <TrendingUp className="w-5 h-5 text-teal-500" />
                  <span className="text-xs">VA Comparison</span>
                </Button>
              </Link>
            </div>
            
            {/* Data Management */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mt-3">
              <Button 
                variant="outline" 
                className="w-full h-16 flex flex-col items-center justify-center gap-1 border-green-200 hover:bg-green-50" 
                onClick={() => setShowSeedDialog(true)}
                data-testid="admin-seed-data-btn"
              >
                <Zap className="w-5 h-5 text-green-500" />
                <span className="text-xs">Seed Demo Data</span>
              </Button>
              <Button 
                variant="outline" 
                className="w-full h-16 flex flex-col items-center justify-center gap-1 border-red-200 hover:bg-red-50" 
                onClick={() => setShowCleanupDialog(true)}
                data-testid="admin-cleanup-data-btn"
              >
                <Trash2 className="w-5 h-5 text-red-500" />
                <span className="text-xs">Cleanup Demo</span>
              </Button>
              <Button 
                variant="outline" 
                className="w-full h-16 flex flex-col items-center justify-center gap-1 border-orange-200 hover:bg-orange-50" 
                onClick={() => setShowDeleteUserDialog(true)}
                data-testid="admin-delete-user-btn"
              >
                <UserMinus className="w-5 h-5 text-orange-500" />
                <span className="text-xs">Delete User</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* VA Bandwidth Overview */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            VA Bandwidth Overview
          </CardTitle>
          <Badge variant="outline" className="text-xs">
            {vaBandwidth.length} VAs
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[300px] overflow-y-auto">
            {vaBandwidth.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No VA bandwidth data available</p>
            ) : (
              vaBandwidth.slice(0, 6).map((va) => (
                <div 
                  key={va.va_id} 
                  className="flex flex-wrap sm:flex-nowrap items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid={`va-bandwidth-row-${va.va_id}`}
                >
                  <img
                    src={va.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${va.va_name || 'VA'}`}
                    alt={va.va_name}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full shrink-0 object-cover"
                    onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${va.va_name || 'VA'}`; }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm sm:text-base text-gray-900 truncate">{va.va_name}</p>
                    <p className="text-xs text-gray-500 truncate">{va.va_email}</p>
                  </div>
                  <BandwidthBadge
                    hoursAvailable={va.hours_available_today}
                    dailyHours={va.daily_hours}
                    hoursUsedToday={va.hours_used_today}
                    hoursAvailableForBooking={va.hours_available_for_booking}
                    maxDailyCapacity={va.max_daily_capacity}
                    planName={va.plan_name}
                    allocationsCount={va.allocations_count}
                    size="default"
                    showProgress={false}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedVAForReserve(va);
                      setShowReserveDialog(true);
                    }}
                    data-testid={`reserve-va-btn-${va.va_id}`}
                  >
                    Reserve
                  </Button>
                </div>
              ))
            )}
          </div>
          {vaBandwidth.length > 6 && (
            <Link to="/team" className="block mt-4">
              <Button variant="outline" className="w-full">
                View All VAs ({vaBandwidth.length})
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle>Recent Activity</CardTitle>
            <span className="text-xs text-gray-500">Live updates</span>
          </CardHeader>
          <CardContent>
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="activity-filter-tabs">
              {[
                { key: 'all', label: 'All' },
                { key: 'task', label: 'Tasks' },
                { key: 'leave', label: 'Leaves' },
                { key: 'payment', label: 'Payments' },
                { key: 'user', label: 'Users' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setActivityFilter(f.key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    activityFilter === f.key
                      ? 'bg-[#5FD068] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`activity-filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="space-y-3">
              {(() => {
                const filtered = activityFilter === 'all'
                  ? recentActivity
                  : recentActivity.filter(a => {
                      if (activityFilter === 'task') return a.type?.includes('task');
                      if (activityFilter === 'leave') return a.type?.includes('leave');
                      if (activityFilter === 'payment') return a.type?.includes('payment');
                      if (activityFilter === 'user') return a.type?.includes('user');
                      return true;
                    });
                return filtered.length > 0 ? filtered.map((activity, idx) => (
                <Link
                  key={`${activity.type}-${idx}`}
                  to={activity.type === 'task_completed' ? '/tasks' : activity.type === 'leave_request' ? '/leave' : activity.type === 'payment' ? '/billing' : activity.type === 'user_joined' ? '/admin/users' : '/tasks'}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  data-testid={`activity-item-${idx}`}
                >
                  <div className="bg-white p-2 rounded-full shadow-sm">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {activity.time ? new Date(activity.time).toLocaleString('en-US', { 
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit', hour12: true 
                      }) : ''}
                    </p>
                  </div>
                </Link>
              )) : (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">{activityFilter === 'all' ? 'No recent activity' : `No ${activityFilter} activity`}</p>
                  <p className="text-sm text-gray-400 mt-1">Activity will appear here as users interact with the system</p>
                </div>
              );
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Pending Approvals</CardTitle>
            <Link to="/leave" className="text-sm text-blue-500 hover:text-blue-600">
              View All
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingLeaves.length > 0 ? pendingLeaves.map((leave) => (
                <div key={leave.id || leave._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{leave.va_name}</h4>
                    <p className="text-sm text-gray-600">
                      {leave.leave_type} • {leave.days_requested} day(s)
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {leave.start_date} to {leave.end_date}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-green-500 hover:bg-green-600 text-white"
                      disabled={approveLoading === (leave.id || leave._id)}
                      onClick={() => handleApproveLeave(leave.id || leave._id)}
                      data-testid={`dashboard-approve-leave-${leave.id || leave._id}`}
                    >
                      {approveLoading === (leave.id || leave._id) ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Approve'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      disabled={approveLoading === (leave.id || leave._id)}
                      onClick={() => handleRejectLeave(leave.id || leave._id)}
                      data-testid={`dashboard-reject-leave-${leave.id || leave._id}`}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
                  <p className="text-gray-500">No pending approvals</p>
                  <p className="text-sm text-gray-400 mt-1">All caught up!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health */}
      <Card className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border-none">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-full shadow-sm">
                  <Activity className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">System Status: Healthy</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    All services are running smoothly. Last checked: Just now
                  </p>
                </div>
              </div>
              <Link to="/performance">
                <Button variant="outline" data-testid="view-analytics-btn">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Analytics
                </Button>
              </Link>
            </div>
            
            {/* Email Service Status Badge */}
            <div className="flex items-center gap-3 bg-white/80 rounded-lg p-3" data-testid="email-status-badge">
              <Mail className={`w-5 h-5 ${emailStatus?.valid ? 'text-green-600' : 'text-orange-500'}`} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Email Service (Postmark)</span>
                  {emailStatus?.valid ? (
                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  ) : emailStatus?.configured ? (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Invalid Token
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Not Configured
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {emailStatus?.valid 
                    ? `Server: ${emailStatus.server_name || 'Active'} • Sender: ${emailStatus.sender_email || 'Configured'}`
                    : emailStatus?.message || 'Configure Postmark for email notifications'
                  }
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6 text-center" data-testid="task-completion-rate">
            <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {stats.totalTasks > 0 ? Math.round((stats.completedTasks / stats.totalTasks) * 100) : (stats.totalTasks === 0 ? 100 : 0)}%
            </p>
            <p className="text-sm text-[#4B5563]">Task Completion Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center" data-testid="avg-va-rating">
            <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.avgVARating || '5.0'}</p>
            <p className="text-sm text-gray-600">Avg. VA Rating</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6 text-center" data-testid="avg-response-time">
            <Clock className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime || '0h'}</p>
            <p className="text-sm text-gray-600">Avg. Response Time</p>
          </CardContent>
        </Card>
      </div>

      {/* Cleanup Demo Data Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Clean Demo Data
            </DialogTitle>
            <DialogDescription>
              This will permanently remove all demo data from the database including:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>Only demo/test users (Demo, Happy, Testt, etc.)</li>
              <li>Tasks assigned to/by demo users</li>
              <li>Team memberships for demo users</li>
              <li>Attendance and EOD reports for demo users</li>
            </ul>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                <strong>Real users (VAs and clients you added) will be preserved.</strong>
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Note:</strong> Only removes users with demo names or test email patterns.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCleanupDialog(false)}
                disabled={cleanupLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleCleanupDemoData}
                disabled={cleanupLoading}
                data-testid="confirm-cleanup-btn"
              >
                {cleanupLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Yes, Clean All Demo Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Seed Demo Data Dialog */}
      <Dialog open={showSeedDialog} onOpenChange={setShowSeedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-green-500" />
              Seed Demo Data
            </DialogTitle>
            <DialogDescription>
              This will create demo data for testing purposes:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              <li>3 Demo VAs (Sarah, Raj, Emily) with different skills</li>
              <li>2 Demo Clients (TechStart Inc, GlobalTrade LLC)</li>
              <li>6 Sample tasks with different statuses</li>
              <li>EOD reports for the last 3 days</li>
              <li>Bandwidth allocations (Premium & Basic plans)</li>
            </ul>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Demo Credentials:</strong> Password for all demo users is <code className="bg-blue-100 px-1 rounded">Demo123!</code>
              </p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                <strong>Note:</strong> All demo data is marked and can be easily cleaned up later.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowSeedDialog(false)}
                disabled={seedLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleSeedDemoData}
                disabled={seedLoading}
                data-testid="confirm-seed-btn"
              >
                {seedLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Create Demo Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User by Email Dialog */}
      <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-orange-600 flex items-center gap-2">
              <UserMinus className="w-5 h-5" />
              Delete User by Email
            </DialogTitle>
            <DialogDescription>
              Enter the email address of the user you want to delete. This will remove the user and ALL their related data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-email">User Email Address</Label>
              <Input
                id="delete-email"
                type="email"
                placeholder="user@example.com"
                value={deleteUserEmail}
                onChange={(e) => setDeleteUserEmail(e.target.value)}
                data-testid="delete-user-email-input"
              />
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-sm text-red-600 mt-1">
                <li>The user account</li>
                <li>All tasks assigned to/by this user</li>
                <li>All attendance and EOD reports</li>
                <li>All ratings and notifications</li>
              </ul>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteUserDialog(false);
                  setDeleteUserEmail('');
                }}
                disabled={deleteUserLoading}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleDeleteUserByEmail}
                disabled={deleteUserLoading || !deleteUserEmail.trim()}
                data-testid="confirm-delete-user-btn"
              >
                {deleteUserLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <UserMinus className="w-4 h-4 mr-2" />
                    Delete User
                  </>
                )}
              </Button>
            </div>
            <div className="border-t pt-4 mt-2">
              <p className="text-xs text-gray-500 mb-2">
                If "Email already registered" error persists after deletion, use Force Clean:
              </p>
              <Button
                variant="outline"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleForceCleanEmail}
                disabled={deleteUserLoading || !deleteUserEmail.trim()}
                data-testid="force-clean-email-btn"
              >
                {deleteUserLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cleaning...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Force Clean Email (removes ALL references)
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reserve VA Dialog */}
      <Dialog open={showReserveDialog} onOpenChange={setShowReserveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Reserve VA (Admin)
            </DialogTitle>
            <DialogDescription>
              Reserve this VA for your use without a subscription. Admin reservations have unlimited bandwidth.
            </DialogDescription>
          </DialogHeader>
          
          {selectedVAForReserve && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <img
                  src={selectedVAForReserve.avatar}
                  alt={selectedVAForReserve.va_name}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <p className="font-medium text-gray-900">{selectedVAForReserve.va_name}</p>
                  <p className="text-sm text-gray-500">{selectedVAForReserve.va_email}</p>
                </div>
              </div>
              
              <BandwidthBadge
                hoursAvailable={selectedVAForReserve.hours_available_today}
                dailyHours={selectedVAForReserve.daily_hours}
                hoursUsedToday={selectedVAForReserve.hours_used_today}
                planName={selectedVAForReserve.plan_name}
                size="detailed"
              />
              
              <div className="space-y-2">
                <Label>Allocation Plan</Label>
                <Select value={reservePlan} onValueChange={setReservePlan}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic Plan (2h/day) - $699/mo</SelectItem>
                    <SelectItem value="premium">Premium Plan (4h/day) - $1,599/mo</SelectItem>
                    <SelectItem value="business">Business Plan (6h/day) - $2,299/mo</SelectItem>
                    <SelectItem value="pro">Pro Plan (8h/day) - $2,999/mo</SelectItem>
                    <SelectItem value="admin_reserved">Admin Reserved (8h/day) - Free</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Plans reference: <a href="https://withnimble.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">withnimble.com</a>
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                As an admin, you can reserve VAs without activating a monthly subscription.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReserveDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#5FD068] hover:bg-[#4ec057] text-white"
              onClick={handleReserveVA}
              disabled={reserveLoading}
              data-testid="confirm-reserve-va-btn"
            >
              {reserveLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reserving...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Reserve VA
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-green-500" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Manually create a new user account (Client, VA, or Admin)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="user-name">Full Name *</Label>
              <Input
                id="user-name"
                placeholder="John Doe"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                data-testid="add-user-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="user-email">Email *</Label>
              <Input
                id="user-email"
                type="email"
                placeholder="john@example.com"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="add-user-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="user-password">Password *</Label>
              <Input
                id="user-password"
                type="password"
                placeholder="Min 8 characters"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                data-testid="add-user-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>User Role *</Label>
              <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                <SelectTrigger data-testid="add-user-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="va">Virtual Assistant (VA)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-[#5FD068] hover:bg-[#4ec057] text-white"
              onClick={handleAddUser}
              disabled={addUserLoading || !newUser.name || !newUser.email || !newUser.password}
              data-testid="confirm-add-user-btn"
            >
              {addUserLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
